import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { TrendingUp, Phone, Clock, ThumbsUp, AlertCircle, Users } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import Sparkline from '@/components/common/Sparkline';
import { loadActiveCompanies, fetchRecentCalls, fetchAiMetrics, computeSummaryMetrics, computeTrends } from '@/lib/services/AIVoiceAgentsMetricsService';

interface CallMetrics {
  total_calls: number;
  successful_calls: number;
  avg_duration: number;
  transfer_rate: number;
  satisfaction_score: number;
}

interface RecentCall {
  id: string;
  call_sid: string;
  caller_phone: string;
  status: string;
  duration: number;
  created_at: string;
  transcript: string;
  company_name: string;
}

interface AIMetric {
  metric_type: string;
  metric_value: number;
  created_at: string;
  company_name: string;
}

export default function PerformanceDashboard() {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('7d');
  const [metrics, setMetrics] = useState<CallMetrics>({
    total_calls: 0,
    successful_calls: 0,
    avg_duration: 0,
    transfer_rate: 0,
    satisfaction_score: 0
  });
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [aiMetrics, setAIMetrics] = useState<AIMetric[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [companies, setCompanies] = useState<any[]>([]);

  // Alert thresholds (adjustable in UI)
  const defaultThresholds = {
    minBookingRate: 60,        // %
    maxTransferRate: 20,       // %
    maxTimeToBookSeconds: 180, // seconds
    maxObjectionCount: 2,      // count
    maxToolLatencyMs: 1200,    // ms
  };
  const [hasThresholdsSupport, setHasThresholdsSupport] = useState(false);
  const [thresholds, setThresholds] = useState(defaultThresholds);

  // Trend arrays for sparklines
  const [trend, setTrend] = useState({
    bookingRate: [] as number[],
    transferRate: [] as number[],
    timeToBook: [] as number[],
    objections: [] as number[],
    toolLatency: [] as number[],
  });

  // Helper to average metric values by type from ai_call_metrics
  const avgMetric = (metricType: string): number => {
    const items = aiMetrics.filter(m => m.metric_type === metricType);
    if (!items.length) return 0;
    return items.reduce((sum, m) => sum + (m.metric_value || 0), 0) / items.length;
  };

  useEffect(() => {
    loadData();
  }, [timeRange, selectedCompany]);

  // Detect if company_configs.ai_alert_thresholds column exists
  useEffect(() => {
    (async () => {
      try {
        const { error } = await supabase
          .from('company_configs')
          .select('ai_alert_thresholds')
          .limit(1);
        setHasThresholdsSupport(!error);
      } catch {
        setHasThresholdsSupport(false);
      }
    })();
  }, []);

  // Load thresholds from DB or localStorage when company or support changes
  useEffect(() => {
    loadThresholds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, hasThresholdsSupport]);

  // Persist thresholds when changed
  useEffect(() => {
    if (selectedCompany === 'all') return;
    const save = async () => {
      if (hasThresholdsSupport) {
        try {
          await supabase
            .from('company_configs')
            .update({ ai_alert_thresholds: thresholds, updated_at: new Date().toISOString() })
            .eq('id', selectedCompany);
        } catch {
          // ignore
        }
      } else {
        try {
          localStorage.setItem(`ai_thresholds:${selectedCompany}`, JSON.stringify(thresholds));
        } catch {
          // ignore
        }
      }
    };
    save();
  }, [thresholds, selectedCompany, hasThresholdsSupport]);

  useEffect(() => {
    const channel = supabase
      .channel('ai_performance_dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_voice_calls' }, () => {
        loadData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_call_metrics' }, () => {
        loadData();
      });

    channel.subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        // ignore
      }
    };
  }, [timeRange, selectedCompany]);

  async function loadThresholds() {
    try {
      if (selectedCompany === 'all') {
        setThresholds(defaultThresholds);
        return;
      }
      if (hasThresholdsSupport) {
        const { data, error } = await supabase
          .from('company_configs')
          .select('ai_alert_thresholds')
          .eq('id', selectedCompany)
          .single();
        if (!error && data && data.ai_alert_thresholds && typeof data.ai_alert_thresholds === 'object') {
          setThresholds({ ...defaultThresholds, ...data.ai_alert_thresholds });
          return;
        }
      }
      try {
        const raw = localStorage.getItem(`ai_thresholds:${selectedCompany}`);
        if (raw) {
          setThresholds({ ...defaultThresholds, ...JSON.parse(raw) });
          return;
        }
      } catch {}
      setThresholds(defaultThresholds);
    } catch {
      setThresholds(defaultThresholds);
    }
  }

  async function loadData() {
    try {
      setLoading(true);

      // Companies
      const comps = await loadActiveCompanies();
      setCompanies(comps);

      // Data
      const calls = await fetchRecentCalls({ timeRange, companyId: selectedCompany });
      const mappedCalls: RecentCall[] = (calls as any[]).map((c) => ({
        id: c.id,
        call_sid: c.call_sid || c.sid || '',
        caller_phone: c.caller_phone || c.from_number || c.from || '',
        status: c.status,
        duration: typeof c.duration === 'number' ? c.duration : (typeof c.duration_seconds === 'number' ? c.duration_seconds : 0),
        created_at: c.created_at,
        transcript: c.transcript || '',
        company_name: c.company_name || c.company_configs?.name || 'Unknown',
      }));
      setRecentCalls(mappedCalls);

      const aiMetricsData = await fetchAiMetrics({ timeRange, companyId: selectedCompany });
      const mappedMetrics: AIMetric[] = (aiMetricsData as any[]).map((m) => ({
        metric_type: m.metric_type,
        metric_value: m.metric_value,
        created_at: m.created_at,
        company_name: m.company_name || m.company_configs?.name || '',
      }));
      setAIMetrics(mappedMetrics);

      // Summary metrics
      setMetrics(computeSummaryMetrics(calls));

      // Trends
      const trends = computeTrends({ calls, aiMetrics: aiMetricsData, timeRange });
      setTrend(trends);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }

  function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function formatPhoneNumber(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  }

  // Derived instrumentation KPIs from ai_call_metrics (fallback-safe)
  const bookingRateFromMetrics = avgMetric('booking_rate'); // percentage 0-100 if present
  const avgTimeToBookSeconds = avgMetric('time_to_book_seconds');
  const avgObjectionCount = avgMetric('objection_count');
  const avgToolLatencyMs = avgMetric('tool_latency_ms');


  // Derived alerts
  const alerts: { type: string; message: string }[] = [];
  if (Math.round(bookingRateFromMetrics || 0) < thresholds.minBookingRate) {
    alerts.push({ type: 'booking', message: `Booking rate low: ${Math.round(bookingRateFromMetrics || 0)}% < ${thresholds.minBookingRate}%` });
  }
  if ((metrics.transfer_rate || 0) > thresholds.maxTransferRate) {
    alerts.push({ type: 'transfer', message: `Transfer rate high: ${metrics.transfer_rate}% > ${thresholds.maxTransferRate}%` });
  }
  if (Math.round(avgTimeToBookSeconds || 0) > thresholds.maxTimeToBookSeconds) {
    alerts.push({ type: 'time', message: `Avg time to book high: ${formatDuration(Math.round(avgTimeToBookSeconds || 0))} > ${formatDuration(thresholds.maxTimeToBookSeconds)}` });
  }
  if ((avgObjectionCount || 0) > thresholds.maxObjectionCount) {
    alerts.push({ type: 'objections', message: `Avg objections high: ${avgObjectionCount.toFixed(2)} > ${thresholds.maxObjectionCount}` });
  }
  if (Math.round(avgToolLatencyMs || 0) > thresholds.maxToolLatencyMs) {
    alerts.push({ type: 'latency', message: `Avg tool latency high: ${Math.round(avgToolLatencyMs || 0)}ms > ${thresholds.maxToolLatencyMs}ms` });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div 
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: theme.colors.primaryColor }}
        />
      </div>
    );
  }

  return (
    <div className="p-6" style={{ backgroundColor: theme.colors.bgPage }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            AI Agent Performance
          </h1>
          <p style={{ color: theme.colors.textSecondary }}>
            Track metrics and analyze agent performance
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Company Filter */}
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-4 py-2 rounded-lg focus:ring-2"
            style={{
              backgroundColor: theme.colors.bgCard,
              border: `1px solid ${theme.colors.borderColor}`,
              color: theme.colors.textPrimary
            }}
          >
            <option value="all">All Companies</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>

          {/* Time Range Filter */}
          <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${theme.colors.borderColor}` }}>
            <button
              onClick={() => setTimeRange('24h')}
              className="px-4 py-2"
              style={{
                backgroundColor: timeRange === '24h' ? theme.colors.primaryColor : theme.colors.bgCard,
                color: timeRange === '24h' ? '#ffffff' : theme.colors.textPrimary
              }}
              onMouseEnter={(e) => {
                if (timeRange !== '24h') e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }}
              onMouseLeave={(e) => {
                if (timeRange !== '24h') e.currentTarget.style.backgroundColor = theme.colors.bgCard;
              }}
            >
              24h
            </button>
            <button
              onClick={() => setTimeRange('7d')}
              className="px-4 py-2"
              style={{
                backgroundColor: timeRange === '7d' ? theme.colors.primaryColor : theme.colors.bgCard,
                color: timeRange === '7d' ? '#ffffff' : theme.colors.textPrimary,
                borderLeft: `1px solid ${theme.colors.borderColor}`,
                borderRight: `1px solid ${theme.colors.borderColor}`
              }}
              onMouseEnter={(e) => {
                if (timeRange !== '7d') e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }}
              onMouseLeave={(e) => {
                if (timeRange !== '7d') e.currentTarget.style.backgroundColor = theme.colors.bgCard;
              }}
            >
              7d
            </button>
            <button
              onClick={() => setTimeRange('30d')}
              className="px-4 py-2"
              style={{
                backgroundColor: timeRange === '30d' ? theme.colors.primaryColor : theme.colors.bgCard,
                color: timeRange === '30d' ? '#ffffff' : theme.colors.textPrimary
              }}
              onMouseEnter={(e) => {
                if (timeRange !== '30d') e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }}
              onMouseLeave={(e) => {
                if (timeRange !== '30d') e.currentTarget.style.backgroundColor = theme.colors.bgCard;
              }}
            >
              30d
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Total Calls
            </span>
            <Phone className="w-5 h-5" style={{ color: theme.colors.primaryColor }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {metrics.total_calls}
          </div>
        </div>

        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Success Rate
            </span>
            <ThumbsUp className="w-5 h-5" style={{ color: theme.colors.successText }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {metrics.total_calls > 0
              ? Math.round((metrics.successful_calls / metrics.total_calls) * 100)
              : 0}%
          </div>
        </div>

        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Avg Duration
            </span>
            <Clock className="w-5 h-5" style={{ color: '#9333ea' }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {formatDuration(metrics.avg_duration)}
          </div>
          <div className="mt-2">
            <Sparkline data={trend.timeToBook} height={32} stroke="#9333ea" />
          </div>
        </div>

        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Transfer Rate
            </span>
            <Users className="w-5 h-5" style={{ color: theme.colors.warningText }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {metrics.transfer_rate}%
          </div>
          <div className="mt-2">
            <Sparkline data={trend.transferRate} height={32} stroke={theme.colors.warningText} />
          </div>
        </div>

        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Satisfaction
            </span>
            <TrendingUp className="w-5 h-5" style={{ color: theme.colors.successText }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {metrics.satisfaction_score.toFixed(1)}/5.0
          </div>
        </div>
      </div>

      {/* Instrumentation KPIs */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6"
      >
        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Booking Rate
            </span>
            <ThumbsUp className="w-5 h-5" style={{ color: theme.colors.successText }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {Math.round(bookingRateFromMetrics || 0)}%
          </div>
          <div className="mt-2">
            <Sparkline data={trend.bookingRate} height={32} stroke={theme.colors.successText} />
          </div>
        </div>

        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Avg Time to Book
            </span>
            <Clock className="w-5 h-5" style={{ color: '#6366f1' }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {formatDuration(Math.round(avgTimeToBookSeconds))}
          </div>
        </div>

        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Avg Objections
            </span>
            <AlertCircle className="w-5 h-5" style={{ color: theme.colors.warningText }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {avgObjectionCount.toFixed(2)}
          </div>
          <div className="mt-2">
            <Sparkline data={trend.objections} height={32} stroke={theme.colors.warningText} />
          </div>
        </div>

        <div 
          className="rounded-lg p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
              Avg Tool Latency
            </span>
            <Clock className="w-5 h-5" style={{ color: '#10b981' }} />
          </div>
          <div className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {Math.round(avgToolLatencyMs)} ms
          </div>
          <div className="mt-2">
            <Sparkline data={trend.toolLatency} height={32} stroke="#10b981" />
          </div>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div 
          className="rounded-lg mb-6 p-6"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" style={{ color: theme.colors.warningText }} />
              <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                Alerts ({alerts.length})
              </h2>
            </div>
            {/* Threshold controls */}
            <div className="flex items-center gap-2 text-sm" style={{ color: theme.colors.textSecondary }}>
              <span>Min Booking %</span>
              <input
                type="number"
                value={thresholds.minBookingRate}
                onChange={(e) => setThresholds({ ...thresholds, minBookingRate: Number(e.target.value) })}
                className="w-16 px-2 py-1 rounded"
                style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}`, color: theme.colors.textPrimary }}
              />
              <span>Max Transfer %</span>
              <input
                type="number"
                value={thresholds.maxTransferRate}
                onChange={(e) => setThresholds({ ...thresholds, maxTransferRate: Number(e.target.value) })}
                className="w-16 px-2 py-1 rounded"
                style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}`, color: theme.colors.textPrimary }}
              />
              <span>Max Book (s)</span>
              <input
                type="number"
                value={thresholds.maxTimeToBookSeconds}
                onChange={(e) => setThresholds({ ...thresholds, maxTimeToBookSeconds: Number(e.target.value) })}
                className="w-20 px-2 py-1 rounded"
                style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}`, color: theme.colors.textPrimary }}
              />
              <span>Max Obj</span>
              <input
                type="number"
                value={thresholds.maxObjectionCount}
                onChange={(e) => setThresholds({ ...thresholds, maxObjectionCount: Number(e.target.value) })}
                className="w-16 px-2 py-1 rounded"
                style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}`, color: theme.colors.textPrimary }}
              />
              <span>Max Lat (ms)</span>
              <input
                type="number"
                value={thresholds.maxToolLatencyMs}
                onChange={(e) => setThresholds({ ...thresholds, maxToolLatencyMs: Number(e.target.value) })}
                className="w-24 px-2 py-1 rounded"
                style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}`, color: theme.colors.textPrimary }}
              />
            </div>
          </div>
          <ul className="space-y-2">
            {alerts.map((a, idx) => (
              <li
                key={idx}
                className="px-3 py-2 rounded border"
                style={{ border: `1px solid ${theme.colors.warningText}`, color: theme.colors.textPrimary, backgroundColor: theme.colors.warningBg }}
              >
                {a.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent Calls Table */}
      <div 
        className="rounded-lg mb-6"
        style={{
          backgroundColor: theme.colors.bgCard,
          border: `1px solid ${theme.colors.borderColor}`
        }}
      >
        <div 
          className="px-6 py-4"
          style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}
        >
          <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
            Recent Calls
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: theme.colors.bgHover, borderBottom: `1px solid ${theme.colors.borderColor}` }}>
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  style={{ color: theme.colors.textSecondary }}
                >
                  Time
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  style={{ color: theme.colors.textSecondary }}
                >
                  Company
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  style={{ color: theme.colors.textSecondary }}
                >
                  Caller
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  style={{ color: theme.colors.textSecondary }}
                >
                  Duration
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  style={{ color: theme.colors.textSecondary }}
                >
                  Status
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  style={{ color: theme.colors.textSecondary }}
                >
                  Transcript Preview
                </th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: theme.colors.bgCard }}>
              {recentCalls.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center" style={{ color: theme.colors.textSecondary }}>
                    <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: theme.colors.textTertiary }} />
                    <p>No calls found for the selected time range</p>
                  </td>
                </tr>
              ) : (
                recentCalls.map((call) => (
                  <tr 
                    key={call.id}
                    style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgCard}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                      {new Date(call.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textSecondary }}>
                      {call.company_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                      {formatPhoneNumber(call.caller_phone)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textSecondary }}>
                      {formatDuration(call.duration)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className="inline-flex px-2 py-1 text-xs rounded-full"
                        style={
                          call.status === 'completed'
                            ? { backgroundColor: theme.colors.successBg, color: theme.colors.successText }
                            : call.status === 'failed'
                            ? { backgroundColor: theme.colors.dangerBg, color: theme.colors.dangerText }
                            : { backgroundColor: theme.colors.warningBg, color: theme.colors.warningText }
                        }
                      >
                        {call.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm max-w-md truncate" style={{ color: theme.colors.textSecondary }}>
                      {call.transcript || 'No transcript available'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Metrics */}
      {aiMetrics.length > 0 && (
        <div 
          className="rounded-lg"
          style={{
            backgroundColor: theme.colors.bgCard,
            border: `1px solid ${theme.colors.borderColor}`
          }}
        >
          <div 
            className="px-6 py-4"
            style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}
          >
            <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
              AI Performance Metrics
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(
                aiMetrics.reduce((acc, metric) => {
                  if (!acc[metric.metric_type]) {
                    acc[metric.metric_type] = {
                      total: 0,
                      count: 0
                    };
                  }
                  acc[metric.metric_type].total += metric.metric_value;
                  acc[metric.metric_type].count += 1;
                  return acc;
                }, {} as Record<string, { total: number; count: number }>)
              ).map(([metricType, data]) => (
                <div 
                  key={metricType} 
                  className="rounded-lg p-4"
                  style={{ border: `1px solid ${theme.colors.borderColor}` }}
                >
                  <div 
                    className="text-sm font-medium mb-1 capitalize"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    {metricType.replace(/_/g, ' ')}
                  </div>
                  <div className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>
                    {(data.total / data.count).toFixed(2)}
                  </div>
                  <div className="text-xs mt-1" style={{ color: theme.colors.textTertiary }}>
                    {data.count} samples
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
